import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faBoxOpen,
  faFileAlt,
  faListAlt,
  faShieldAlt,
  faLayerGroup,
  faObjectGroup,
  faMapMarkerAlt,
  faCommentDots,
  faEnvelope,
  faCalculator,
  faClipboardList,
  faMagnifyingGlass,
  faCircleInfo,
  faTriangleExclamation,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import './faqAdmin.scss';

type TabKey =
  | 'all'
  | 'user_access'
  | 'applications'
  | 'configuration'
  | 'system';

interface ModuleFeature {
  text: string;
}

interface ModuleStep {
  text: string;
}

interface AdminModule {
  id: string;
  tab: TabKey;
  icon: typeof faUsers;
  titleKey: string;
  descKey: string;
  features: ModuleFeature[];
  steps?: ModuleStep[];
  stepsLabelKey?: string;
  noteKey?: string;
  noteVariant?: 'info' | 'warning';
  subSections?: {
    titleKey: string;
    descKey: string;
    features: ModuleFeature[];
  }[];
  permissionBadges?: string[];
  extraKey?: string;
  extraItems?: string[];
}

const FaqAdmin = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const modules: AdminModule[] = useMemo(
    () => [
      {
        id: 'user_management',
        tab: 'user_access',
        icon: faUsers,
        titleKey: 'faq_admin.user_management.title',
        descKey: 'faq_admin.user_management.description',
        features: [
          { text: t('faq_admin.user_management.feature_1') },
          { text: t('faq_admin.user_management.feature_2') },
          { text: t('faq_admin.user_management.feature_3') },
          { text: t('faq_admin.user_management.feature_4') },
          { text: t('faq_admin.user_management.feature_5') },
          { text: t('faq_admin.user_management.feature_6') }
        ],
        steps: [
          { text: t('faq_admin.user_management.how_to_step_1') },
          { text: t('faq_admin.user_management.how_to_step_2') },
          { text: t('faq_admin.user_management.how_to_step_3') },
          { text: t('faq_admin.user_management.how_to_step_4') }
        ],
        stepsLabelKey: 'faq_admin.user_management.how_to_create',
        permissionBadges: ['view', 'create', 'edit']
      },
      {
        id: 'role_management',
        tab: 'user_access',
        icon: faShieldAlt,
        titleKey: 'faq_admin.role_management.title',
        descKey: 'faq_admin.role_management.description',
        features: [
          { text: t('faq_admin.role_management.feature_1') },
          { text: t('faq_admin.role_management.feature_2') },
          { text: t('faq_admin.role_management.feature_3') },
          { text: t('faq_admin.role_management.feature_4') },
          { text: t('faq_admin.role_management.feature_5') }
        ],
        extraKey: 'faq_admin.role_management.modules_label',
        extraItems: [
          t('faq_admin.role_management.module_1'),
          t('faq_admin.role_management.module_2'),
          t('faq_admin.role_management.module_3'),
          t('faq_admin.role_management.module_4'),
          t('faq_admin.role_management.module_5'),
          t('faq_admin.role_management.module_6'),
          t('faq_admin.role_management.module_7'),
          t('faq_admin.role_management.module_8'),
          t('faq_admin.role_management.module_9'),
          t('faq_admin.role_management.module_10'),
          t('faq_admin.role_management.module_11')
        ],
        permissionBadges: ['view', 'create', 'edit', 'delete']
      },
      {
        id: 'admin_level',
        tab: 'user_access',
        icon: faLayerGroup,
        titleKey: 'faq_admin.admin_level.title',
        descKey: 'faq_admin.admin_level.description',
        features: [
          { text: t('faq_admin.admin_level.feature_1') },
          { text: t('faq_admin.admin_level.feature_2') },
          { text: t('faq_admin.admin_level.feature_3') },
          { text: t('faq_admin.admin_level.feature_4') }
        ],
        noteKey: 'faq_admin.admin_level.note',
        noteVariant: 'info',
        permissionBadges: ['view', 'create', 'edit']
      },
      {
        id: 'group_management',
        tab: 'user_access',
        icon: faObjectGroup,
        titleKey: 'faq_admin.group_management.title',
        descKey: 'faq_admin.group_management.description',
        features: [
          { text: t('faq_admin.group_management.feature_1') },
          { text: t('faq_admin.group_management.feature_2') },
          { text: t('faq_admin.group_management.feature_3') },
          { text: t('faq_admin.group_management.feature_4') }
        ],
        noteKey: 'faq_admin.group_management.note',
        noteVariant: 'info',
        permissionBadges: ['view', 'create', 'edit']
      },
      {
        id: 'federal_state',
        tab: 'user_access',
        icon: faMapMarkerAlt,
        titleKey: 'faq_admin.federal_state.title',
        descKey: 'faq_admin.federal_state.description',
        features: [
          { text: t('faq_admin.federal_state.feature_1') },
          { text: t('faq_admin.federal_state.feature_2') },
          { text: t('faq_admin.federal_state.feature_3') },
          { text: t('faq_admin.federal_state.feature_4') }
        ],
        permissionBadges: ['view', 'create', 'edit']
      },
      {
        id: 'application_management',
        tab: 'applications',
        icon: faFileAlt,
        titleKey: 'faq_admin.application_management.title',
        descKey: 'faq_admin.application_management.description',
        features: [
          { text: t('faq_admin.application_management.feature_1') },
          { text: t('faq_admin.application_management.feature_2') },
          { text: t('faq_admin.application_management.feature_3') },
          { text: t('faq_admin.application_management.feature_4') },
          { text: t('faq_admin.application_management.feature_5') },
          { text: t('faq_admin.application_management.feature_6') }
        ],
        extraKey: 'faq_admin.application_management.statuses',
        extraItems: [
          t('faq_admin.application_management.status_1'),
          t('faq_admin.application_management.status_2'),
          t('faq_admin.application_management.status_3'),
          t('faq_admin.application_management.status_4')
        ],
        permissionBadges: ['view']
      },
      {
        id: 'application_type_option',
        tab: 'applications',
        icon: faListAlt,
        titleKey: 'faq_admin.application_type_option.title',
        descKey: 'faq_admin.application_type_option.description',
        features: [
          { text: t('faq_admin.application_type_option.feature_1') },
          { text: t('faq_admin.application_type_option.feature_2') },
          { text: t('faq_admin.application_type_option.feature_3') },
          { text: t('faq_admin.application_type_option.feature_4') }
        ],
        steps: [
          { text: t('faq_admin.application_type_option.how_to_step_1') },
          { text: t('faq_admin.application_type_option.how_to_step_2') },
          { text: t('faq_admin.application_type_option.how_to_step_3') },
          { text: t('faq_admin.application_type_option.how_to_step_4') },
          { text: t('faq_admin.application_type_option.how_to_step_5') }
        ],
        stepsLabelKey: 'faq_admin.application_type_option.how_to_create',
        permissionBadges: ['view', 'create', 'edit']
      },
      {
        id: 'product_management',
        tab: 'applications',
        icon: faBoxOpen,
        titleKey: 'faq_admin.product_management.title',
        descKey: 'faq_admin.product_management.description',
        features: [
          { text: t('faq_admin.product_management.feature_1') },
          { text: t('faq_admin.product_management.feature_2') },
          { text: t('faq_admin.product_management.feature_3') },
          { text: t('faq_admin.product_management.feature_4') },
          { text: t('faq_admin.product_management.feature_5') }
        ],
        extraKey: 'faq_admin.product_management.refund_policies',
        extraItems: [
          t('faq_admin.product_management.policy_1'),
          t('faq_admin.product_management.policy_2'),
          t('faq_admin.product_management.policy_3'),
          t('faq_admin.product_management.policy_4')
        ],
        permissionBadges: ['view', 'create', 'edit']
      },
      {
        id: 'subsidy_calculation',
        tab: 'configuration',
        icon: faCalculator,
        titleKey: 'faq_admin.subsidy_calculation.title',
        descKey: 'faq_admin.subsidy_calculation.description',
        features: [],
        subSections: [
          {
            titleKey: 'faq_admin.subsidy_calculation.with_income_title',
            descKey: 'faq_admin.subsidy_calculation.with_income_desc',
            features: [
              {
                text: t('faq_admin.subsidy_calculation.with_income_feature_1')
              },
              {
                text: t('faq_admin.subsidy_calculation.with_income_feature_2')
              },
              { text: t('faq_admin.subsidy_calculation.with_income_feature_3') }
            ]
          },
          {
            titleKey: 'faq_admin.subsidy_calculation.without_income_title',
            descKey: 'faq_admin.subsidy_calculation.without_income_desc',
            features: [
              {
                text: t(
                  'faq_admin.subsidy_calculation.without_income_feature_1'
                )
              },
              {
                text: t(
                  'faq_admin.subsidy_calculation.without_income_feature_2'
                )
              },
              {
                text: t(
                  'faq_admin.subsidy_calculation.without_income_feature_3'
                )
              }
            ]
          },
          {
            titleKey: 'faq_admin.subsidy_calculation.health_title',
            descKey: 'faq_admin.subsidy_calculation.health_desc',
            features: [
              { text: t('faq_admin.subsidy_calculation.health_feature_1') },
              { text: t('faq_admin.subsidy_calculation.health_feature_2') },
              { text: t('faq_admin.subsidy_calculation.health_feature_3') },
              { text: t('faq_admin.subsidy_calculation.health_feature_4') },
              { text: t('faq_admin.subsidy_calculation.health_feature_5') }
            ]
          },
          {
            titleKey: 'faq_admin.subsidy_calculation.calculator_title',
            descKey: 'faq_admin.subsidy_calculation.calculator_desc',
            features: [
              { text: t('faq_admin.subsidy_calculation.calculator_feature_1') },
              { text: t('faq_admin.subsidy_calculation.calculator_feature_2') },
              { text: t('faq_admin.subsidy_calculation.calculator_feature_3') }
            ]
          }
        ],
        permissionBadges: ['view', 'edit']
      },
      {
        id: 'tooltip_management',
        tab: 'configuration',
        icon: faCommentDots,
        titleKey: 'faq_admin.tooltip_management.title',
        descKey: 'faq_admin.tooltip_management.description',
        features: [
          { text: t('faq_admin.tooltip_management.feature_1') },
          { text: t('faq_admin.tooltip_management.feature_2') },
          { text: t('faq_admin.tooltip_management.feature_3') }
        ],
        noteKey: 'faq_admin.tooltip_management.note',
        noteVariant: 'warning',
        permissionBadges: ['view', 'edit']
      },
      {
        id: 'smtp_config',
        tab: 'system',
        icon: faEnvelope,
        titleKey: 'faq_admin.smtp_config.title',
        descKey: 'faq_admin.smtp_config.description',
        features: [
          { text: t('faq_admin.smtp_config.feature_1') },
          { text: t('faq_admin.smtp_config.feature_2') },
          { text: t('faq_admin.smtp_config.feature_3') }
        ],
        noteKey: 'faq_admin.smtp_config.note',
        noteVariant: 'warning',
        permissionBadges: ['view', 'edit']
      },
      {
        id: 'logs',
        tab: 'system',
        icon: faClipboardList,
        titleKey: 'faq_admin.logs.title',
        descKey: 'faq_admin.logs.description',
        features: [
          { text: t('faq_admin.logs.feature_1') },
          { text: t('faq_admin.logs.feature_2') },
          { text: t('faq_admin.logs.feature_3') }
        ],
        permissionBadges: ['view']
      }
    ],
    [t]
  );

  const filteredModules = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    return modules.filter(module => {
      const matchesTab = activeTab === 'all' || module.tab === activeTab;
      if (!matchesTab) return false;
      if (!query) return true;
      const title = t(module.titleKey).toLowerCase();
      const desc = t(module.descKey).toLowerCase();
      const featureText = module.features
        .map(f => f.text.toLowerCase())
        .join(' ');
      return (
        title.includes(query) ||
        desc.includes(query) ||
        featureText.includes(query)
      );
    });
  }, [modules, activeTab, searchQuery, t]);

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'all', label: t('faq_admin.tabs.all') },
    { key: 'user_access', label: t('faq_admin.tabs.user_access') },
    { key: 'applications', label: t('faq_admin.tabs.applications') },
    { key: 'configuration', label: t('faq_admin.tabs.configuration') },
    { key: 'system', label: t('faq_admin.tabs.system') }
  ];

  return (
    <div className="fadmin-page">
      {/* Header */}
      <div className="fadmin-header-card">
        <div>
          <h2 className="fadmin-header-card__title">{t('faq_admin.title')}</h2>
          <p className="fadmin-header-card__subtitle">
            {t('faq_admin.subtitle')}
          </p>
        </div>
        <div className="fadmin-header-card__search">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="fadmin-header-card__search-icon"
          />
          <input
            type="text"
            placeholder={t('faq_admin.search_placeholder')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Overview note */}
      <div className="fadmin-note fadmin-note--info mb-4">
        <FontAwesomeIcon icon={faCircleInfo} className="fadmin-note__icon" />
        <div>
          <strong>{t('faq_admin.overview.getting_started')}: </strong>
          {t('faq_admin.overview.getting_started_desc')}
        </div>
      </div>

      {/* Tabs */}
      <div className="fadmin-tabs">
        {tabs.map(tab => (
          <button
            key={tab.key}
            className={`fadmin-tab${
              activeTab === tab.key ? ' fadmin-tab--active' : ''
            }`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Modules */}
      {filteredModules.length === 0 ? (
        <div className="fadmin-no-results">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="fadmin-no-results__icon"
          />
          <p>{t('faq_admin.no_results')}</p>
        </div>
      ) : (
        <div className="fadmin-accordion-list">
          <Accordion alwaysOpen>
            {filteredModules.map((module, index) => (
              <Accordion.Item key={module.id} eventKey={String(index)}>
                <Accordion.Header>
                  <div className="fadmin-accordion-header">
                    <div className="fadmin-section-ico">
                      <FontAwesomeIcon icon={module.icon} />
                    </div>
                    <span className="fadmin-accordion-header__title">
                      {t(module.titleKey)}
                    </span>
                  </div>
                </Accordion.Header>

                <Accordion.Body>
                  {/* Permission badges — shown only when expanded */}
                  {module.permissionBadges && (
                    <div className="fadmin-badges">
                      {module.permissionBadges.map(badge => (
                        <span
                          key={badge}
                          className={`fadmin-tag fadmin-tag--${badge}`}
                        >
                          {badge.charAt(0).toUpperCase() + badge.slice(1)}
                        </span>
                      ))}
                    </div>
                  )}

                  <p className="fadmin-desc">{t(module.descKey)}</p>

                  {/* Feature list */}
                  {module.features.length > 0 && (
                    <div className="fadmin-features">
                      <p className="fadmin-section-label">Key Features</p>
                      <ul className="fadmin-feature-list">
                        {module.features.map((feature, i) => (
                          <li key={i} className="fadmin-feature-item">
                            <FontAwesomeIcon
                              icon={faChevronRight}
                              className="fadmin-feature-icon"
                              size="xs"
                            />
                            {feature.text}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Sub-sections */}
                  {module.subSections && module.subSections.length > 0 && (
                    <div className="fadmin-subsections">
                      <Row className="g-3">
                        {module.subSections.map((sub, i) => (
                          <Col key={i} xs={12} md={6}>
                            <div className="fadmin-subsection-card">
                              <p className="fadmin-subsection-title">
                                {t(sub.titleKey)}
                              </p>
                              <p className="fadmin-subsection-desc">
                                {t(sub.descKey)}
                              </p>
                              <ul className="fadmin-feature-list">
                                {sub.features.map((f, j) => (
                                  <li key={j} className="fadmin-feature-item">
                                    <FontAwesomeIcon
                                      icon={faChevronRight}
                                      className="fadmin-feature-icon"
                                      size="xs"
                                    />
                                    {f.text}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}

                  {/* How-to steps */}
                  {module.steps && module.steps.length > 0 && (
                    <div className="fadmin-steps">
                      <p className="fadmin-section-label">
                        {module.stepsLabelKey ? t(module.stepsLabelKey) : ''}
                      </p>
                      <ol className="fadmin-steps-list">
                        {module.steps.map((step, i) => (
                          <li key={i}>{step.text}</li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* Extra items */}
                  {module.extraKey && module.extraItems && (
                    <div className="fadmin-extra">
                      <p className="fadmin-section-label">
                        {t(module.extraKey)}
                      </p>
                      <div className="fadmin-extra-grid">
                        {module.extraItems.map((item, i) => (
                          <span key={i} className="fadmin-extra-item">
                            <span className="fadmin-extra-bullet" />
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Note */}
                  {module.noteKey && (
                    <div
                      className={`fadmin-note fadmin-note--${
                        module.noteVariant ?? 'info'
                      } mt-3`}
                    >
                      <FontAwesomeIcon
                        icon={
                          module.noteVariant === 'warning'
                            ? faTriangleExclamation
                            : faCircleInfo
                        }
                        className="fadmin-note__icon"
                      />
                      <span>{t(module.noteKey)}</span>
                    </div>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </div>
      )}
    </div>
  );
};

export default FaqAdmin;
